import { useMediaSettingsStore, useRoomActionsStore, useRoomStore } from '@src/stores';

import { audioProcessor } from '../services/AudioProcessorService';

import { INFO_MODAL_ICONS } from '@src/constants';

import { EMediaType, TMediaTypes } from '@src/types';

// Device / platform helpers for media handling

/**
 * Detect if the current output device is likely headphones.
 * Uses device label heuristics — not 100% reliable but useful as a hint
 * for auto-suggesting echo cancellation settings.
 */
export const detectHeadphones = (): boolean => {
  const selectedSpeaker = useMediaSettingsStore.getState().mediaSettings.selectedSpeakerDevice;
  if (!selectedSpeaker?.label) return false;
  const label = selectedSpeaker.label.toLowerCase();
  return (
    label.includes('headphone') ||
    label.includes('headset') ||
    label.includes('earphone') ||
    label.includes('airpod') ||
    label.includes('earbud')
  );
};

export const isMacOS = () => /Macintosh|Mac OS X/i.test(navigator.userAgent || '');

export const stopTracks = (stream: MediaStream) => {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
};

export const isAudioContextSupported = () => {
  return !!(window.AudioContext || window.webkitAudioContext);
};

export const hasAudioTrack = (mediaStream: MediaStream) => {
  const audioTracks = mediaStream.getAudioTracks();
  return audioTracks.length > 0;
};

export const isValidVideoStream = (stream: MediaStream): boolean => {
  const videoTracks = stream.getVideoTracks();

  return videoTracks.length > 0;
};

export const updateVolumeIndicator = (volume: number) => {
  const bars = document.querySelectorAll('.volume-bar') as NodeListOf<Element>;
  const activeBars = Math.ceil(volume * bars.length);
  bars.forEach((bar, index) => {
    bar.classList.toggle('active', index < activeBars);
  });
};

export const stopMicrophoneProcessing = (scriptProcessor: ScriptProcessorNode | null) => {
  const bars = document.querySelectorAll('.volume-bar') as NodeListOf<Element>;
  if (scriptProcessor) {
    scriptProcessor.disconnect();
    scriptProcessor = null;
  }
  bars.forEach((bar) => {
    bar.classList.toggle('inactive');
  });
};

export const getMediaStreamSource = (stream: MediaStream, type: TMediaTypes) => {
  let track: MediaStreamTrack = stream.getTracks()[0];
  switch (type) {
    case 'audioType':
      track = stream.getAudioTracks()[0];
      break;
    case 'videoType':
    case 'screenType':
      track = stream.getVideoTracks()[0];
      break;
    default:
      break;
  }
  const consumerStream = new MediaStream();
  consumerStream.addTrack(track);
  return consumerStream;
};

export const applyNoiseCancellation = async (stream: MediaStream) => {
  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) {
    return stream;
  }

  // Fallback: Use Browser's Native Noise Suppression
  // This is much better than a LowPass filter for mobile/low-end devices
  try {
    const track = audioTracks[0];
    const constraints = track.getConstraints();

    // If not already enabled, apply it
    if (!constraints.noiseSuppression) {
      await track.applyConstraints({
        ...constraints,
        noiseSuppression: true,
        echoCancellation: true,
        autoGainControl: true,
      });
    }
  } catch (err) {
    console.warn('Failed to apply native noise suppression:', err);
  }

  return stream;
};

export const applyNoiseCancelledStream = async (inputStream: MediaStream) => {
  return await audioProcessor.processStream(inputStream);
};

export const applyNoiseCancelledStreamImproved = async (inputStream: MediaStream) => {
  if (!inputStream.getAudioTracks().length) return inputStream;

  const audioContext = new AudioContext();

  // Load the worklet processor
  try {
    const workletPath = `${process.env.PUBLIC_URL}/static/js/NoiseSuppressorWorkletImproved.js`;
    await audioContext.audioWorklet.addModule(workletPath);
  } catch (error) {
    console.error('Failed to load worklet:', error);
    return inputStream; // Fallback to original stream
  }

  const source = audioContext.createMediaStreamSource(inputStream);
  const destination = audioContext.createMediaStreamDestination();

  // Create worklet node
  const noiseSuppressor = new AudioWorkletNode(audioContext, 'noise-suppressor-processor');

  // Optional: Add gentle high-pass filtering
  const highPass = audioContext.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = 80; // Remove rumble without affecting voice

  // Connect the nodes
  source.connect(highPass);
  highPass.connect(noiseSuppressor);
  noiseSuppressor.connect(destination);

  return destination.stream;
};

export const checkMediaDevicePermission = async (mediaType: PermissionName): Promise<boolean> => {
  try {
    const valid = await navigator.permissions.query({ name: mediaType });
    return valid.state === 'granted';
  } catch (err) {
    console.warn('Media device check failed:', err);
    return false;
  }
};

export const findNewDevices = (prevDevices: MediaDeviceInfo[], newDevices: MediaDeviceInfo[]) =>
  newDevices.filter((newDevice) => !prevDevices.some((prevDevice) => prevDevice.deviceId === newDevice.deviceId));

export const devicesAreEqual = (prevDevices: MediaDeviceInfo[], newDevices: MediaDeviceInfo[]) => {
  if (prevDevices.length !== newDevices.length) return false;
  const sortById = (list: MediaDeviceInfo[]) =>
    [...list].sort((device1, device2) => device1.deviceId.localeCompare(device2.deviceId));
  const sortedA = sortById(prevDevices);
  const sortedB = sortById(newDevices);
  return sortedA.every(
    (device, i) =>
      device.deviceId === sortedB[i].deviceId && device.label === sortedB[i].label && device.kind === sortedB[i].kind
  );
};

export const forceCameraRelease = async () => {
  try {
    // This sequence helps unlock Samsung cameras
    const tempStream1 = await navigator.mediaDevices.getUserMedia({ video: true });
    const tempStream2 = await navigator.mediaDevices.getUserMedia({ video: true });

    stopTracks(tempStream1);
    stopTracks(tempStream2);
  } catch (err) {
    console.log('Force release attempt failed (may have worked)', err);
  }
};

export const isWebView = () => {
  const userAgent = navigator.userAgent;

  // Standard WebView patterns
  if (/wv|WebView|WKWebView|UIWebView/i.test(userAgent)) {
    return true;
  }

  // iOS native app WebView detection - improved for modern devices
  if (/iPhone|iPad/i.test(userAgent) && /AppleWebKit/i.test(userAgent)) {
    // Check for common WebView indicators
    const hasWebViewIndicators = /wv|WebView|WKWebView|UIWebView/i.test(userAgent);

    // Check if it's likely a WebView by absence of standard browser identifiers
    // But be less strict about Safari presence as modern WebViews might include it
    const hasStandardBrowser = /Safari|Chrome|Firefox|Edge|Opera/i.test(userAgent);

    // Additional check for mobile app patterns
    const hasMobileAppPatterns = /Mobile|App/i.test(userAgent) && !/Safari/i.test(userAgent);

    // Check for specific iOS WebView patterns
    const hasIOSWebViewPatterns =
      /iPhone|iPad/i.test(userAgent) &&
      /AppleWebKit/i.test(userAgent) &&
      /Mobile/i.test(userAgent) &&
      !/Safari/i.test(userAgent);

    if (
      hasWebViewIndicators ||
      hasMobileAppPatterns ||
      hasIOSWebViewPatterns ||
      (/iPhone|iPad/i.test(userAgent) && /AppleWebKit/i.test(userAgent) && !hasStandardBrowser)
    ) {
      return true;
    }
  }

  // Additional check for standalone mode (PWA)
  if ((window.navigator as any).standalone === true) {
    return true;
  }

  return false;
};

export const handleCameraError = (error: Error, mediaType: EMediaType) => {
  if (mediaType !== EMediaType.video) return;
  // Provide more specific error messages based on error type
  let title = 'Camera Access Denied';
  let description = 'Please allow Camera access from your browser settings to start the video.';

  if (error instanceof Error) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      description = 'Camera permission was denied. Please refresh the page and allow camera access.';
    } else if (error.name === 'NotFoundError') {
      title = 'Camera Not Found';
      description = 'No camera device was found. Please check your camera connection.';
    } else if (error.name === 'NotReadableError') {
      title = 'Camera In Use';
      description = 'Camera is being used by another application. Please close other apps and try again.';
    } else if (error.name === 'OverconstrainedError') {
      title = 'Camera Constraints Error';
      description = 'The selected camera does not support the required settings. Try selecting a different camera.';
    }
  }
  useRoomStore.getState().setInfoModalContent({
    icon: INFO_MODAL_ICONS.ACCESS_CAMERA,
    title,
    description,
  });
  useRoomStore.getState().setIsInfoModalOpen(true);
  useRoomActionsStore.getState().setCamSettings({ isCamClosed: true });
};
